'use client';

/**
 * IndexedDB-backed page preferences with a synchronous in-memory facade.
 *
 * PURPOSE:
 * Stores page preferences in the main app database (linqops_db) instead of
 * the HTTP Cookie header. Cookies are sent with EVERY request, so as more
 * pages stored preferences the request headers kept growing until they
 * exceeded the ~10KB Lambda Function URL limit and pages failed with
 * HTTP 413 before reaching the app.
 *
 * STORAGE LAYOUT (no schema change, no version bump):
 * Page preferences live as ordinary rows inside the EXISTING "user_data"
 * object store — the same store used for subscription, employee and chat
 * cache data — under a "pref:" key prefix:
 *
 *   { key: "pref:Employee",   value: {...}, timestamp }
 *   { key: "pref:Timecard",   value: {...}, timestamp }
 *   { key: "pref:Dispatch",   value: {...}, timestamp }
 *
 * WHY NO NEW OBJECT STORE (and no DB_VERSION bump):
 * IndexedDB can only create object stores inside onupgradeneeded, which only
 * fires when the database version INCREASES. Bumping linqops_db to v2 while
 * thousands of long-lived driver/PWA tabs still run v1 code would cause:
 *   - Old tabs can no longer open the DB (VersionError on every access).
 *   - The stale-version recovery in indexedDB.ts treats a higher-version DB
 *     as corrupt and deletes it — wiping user_data AND prefs repeatedly as
 *     old and new tabs fight over the version.
 * Staying at v1 inside an existing store removes all of that risk: every
 * version of the app can open the same database, and leftover "pref:" rows
 * are harmless to older code.
 *
 * WHY THE "pref:" PREFIX:
 *   1. Boot hydration reads ONLY preference rows via a key-range scan
 *      (getItemsByPrefix) — it never loads the large chat caches.
 *   2. It prevents key collisions: "pref:Timecard" can never overwrite an
 *      existing or future "user_data" key, and vice versa.
 *   3. DevTools filtering and future cleanup (delete all prefs at once) is
 *      trivial with a shared prefix.
 *
 * WHY ONE ROW PER PAGE (not one big preferences object):
 * Each tab keeps its own in-memory cache. With a single shared row, two
 * tabs saving different pages would overwrite each other (last write wins).
 * Separate rows make cross-tab writes to different pages safe by design —
 * no atomic-merge transaction helper required.
 *
 * WHY ROUTE ALL DB ACCESS THROUGH indexedDB.ts:
 * indexedDB.ts owns the only linqops_db open path and its version guard
 * (proactive stale-version check + reactive VersionError recovery). By using
 * its setItem/getItemsByPrefix helpers, preference reads/writes get the same
 * protection and there is exactly one place that manages the connection.
 *
 * API mirrors cookies.ts one-to-one:
 *   getPagePreferencesFromCookie      → getPagePreferencesFromIndexedDB
 *   setPagePreferencesInCookie        → setPagePreferencesInIndexedDB
 *   updatePagePreferencesInCookie     → updatePagePreferencesInIndexedDB
 *
 * Reads/writes are synchronous against an in-memory Map that is hydrated
 * once at app boot via initPrefStore(); IDB writes are fire-and-forget.
 * Pages must gate preference reads on isReady() (see usePagePreferences).
 */

import { getItemsByPrefix, setItem } from "./indexedDB";
import { deleteCookie } from "./cookies";
import type { PagePreferences } from "./cookies";

// Module-local storage detail, not a business constant — kept here rather
// than in constants.ts because nothing outside this file needs to know it.
const PREF_KEY_PREFIX = "pref:";

// In-memory cache of preferences, hydrated from IDB at boot.
// Values are stored as JSON strings so every read via JSON.parse returns a
// fresh object — callers may safely mutate the result without corrupting
// the cache or leaking object references between calls.
const prefCache = new Map<string, string>();
// Preference names whose legacy cookie has already been deleted this session.
const clearedCookies = new Set<string>();
let dbReady = false;
let initPromise: Promise<void> | null = null;

/**
 * Initialize the preference store by loading every "pref:*" row from
 * linqops_db into the in-memory Map.
 *
 * INITIALIZATION FLOW:
 * 1. Read all rows whose key starts with "pref:" from "user_data"
 * 2. Strip the prefix and populate prefCache (values are JSON strings)
 * 3. Mark dbReady = true so pages can start reading preferences
 *
 * RACE SAFETY:
 * If a write somehow lands before hydration completes, its key already
 * exists in prefCache — hydration only fills keys that are absent, so the
 * early write is never overwritten by older stored data.
 *
 * IDEMPOTENCY:
 * Safe to call multiple times — only the first call performs work via
 * the cached initPromise. Subsequent calls return the same promise.
 * This handles React StrictMode double-mount and multiple init calls.
 *
 * FALLBACK BEHAVIOR:
 * If IDB fails (private mode, quota exceeded, unsupported browser),
 * falls back to a session-scoped in-memory Map. Preferences won't persist
 * across reloads, but the app continues working.
 */
export function initPrefStore(): Promise<void> {
  if (!initPromise) {
    initPromise = doInit();
  }
  return initPromise;
}

async function doInit(): Promise<void> {
  if (typeof window === "undefined" || typeof indexedDB === "undefined") {
    dbReady = true; // SSR / unsupported — in-memory Map only
    return;
  }

  try {
    // Key-range read over "pref:*" rows only. getItemsByPrefix goes through
    // indexedDB.ts's openDB, so the shared version guard runs first.
    const rows = await getItemsByPrefix<PagePreferences | string>(PREF_KEY_PREFIX);

    // Hydrate the in-memory Map. prefCache stores JSON strings, so objects
    // are stringified; a stored string is kept as-is (defensive: tolerates
    // records written by an earlier code version).
    for (const [fullKey, value] of Object.entries(rows)) {
      const pageName = fullKey.slice(PREF_KEY_PREFIX.length);
      if (!prefCache.has(pageName)) {
        prefCache.set(
          pageName,
          typeof value === "string" ? value : JSON.stringify(value)
        );
      }
    }

    console.log(`[prefStore] Hydrated ${prefCache.size} preferences from linqops_db`);
  } catch (error) {
    console.warn("[prefStore] IDB unavailable, using in-memory Map only:", error);
  } finally {
    // Always mark ready — pages proceed with whatever the Map contains.
    // This ensures pages never hang waiting for initialization.
    dbReady = true;
  }
}

/**
 * True once initPrefStore() has finished (successfully or not).
 * Pages must not read preferences before this is true — reads against an
 * unhydrated Map return null and writes would overwrite stored values.
 */
export function isReady(): boolean {
  return dbReady;
}

// ============================================================================
// Public page-preference API (mirrors cookies.ts)
// ============================================================================

/**
 * Get user preferences for a specific page.
 * @param pageName Name of the page
 * @returns The user preferences for the page, or null if none found
 */
export function getPagePreferencesFromIndexedDB<T extends PagePreferences = PagePreferences>(
  pageName: string
): T | null {
  try {
    // LEGACY COOKIE CLEANUP (migration from cookies to IndexedDB):
    // Pages that migrated to this store used to persist preferences in an
    // HTTP cookie under the same name (pageName === cookie name). Any page
    // calling this getter no longer needs that cookie — the preference is
    // now served from the in-memory cache / IDB — so we delete it here.
    //
    // WHY THIS RUNS BEFORE THE CACHE READ:
    // Previously it only ran on a cache MISS, which meant a cookie survived
    // forever whenever the first read happened to be a hit (e.g. a stored
    // preference already existed in IDB). Placing it unconditionally means
    // the legacy cookie is deleted on the very first read of the session —
    // the earliest moment the page is guaranteed to be on IndexedDB — so
    // the request header shrinks immediately.
    //
    // WHY HERE INSTEAD OF IN EACH PAGE:
    // - The cookie name is already the key we receive, so cleanup is
    //   automatic for every migrated page with zero per-page wiring.
    //
    // WHY THE SET:
    // - This getter is called on every preference read AND internally by
    //   updatePagePreferencesInIndexedDB on every write. deleteCookie is
    //   idempotent, but calling it every time would do a redundant
    //   document.cookie write per access. The Set limits it to one write
    //   per key per session.
    //
    // NOTE ON VALUES: we intentionally do NOT copy cookie values into IDB.
    // Stale cookie values are discarded and the page falls back to defaults
    // for one visit — simpler and safer than migrating a format we no
    // longer control. (The historical Timecard-Review rename is handled
    // separately in the Timecard page.)
    if (!clearedCookies.has(pageName)) {
      clearedCookies.add(pageName);
      deleteCookie(pageName);
    }

    const savedPreferences = prefCache.get(pageName);
    if (savedPreferences) {
      // JSON.parse returns a fresh object each call — safe for callers to
      // mutate the result (e.g. updatePagePreferencesInIndexedDB spreading).
      return JSON.parse(savedPreferences) as T;
    }
  } catch (error) {
    console.error(`Error retrieving preferences for ${pageName}:`, error);
  }
  return null;
}

/**
 * Save user preferences for a specific page (replaces the whole object).
 * @param pageName Name of the page
 * @param preferences Preferences to save
 */
export function setPagePreferencesInIndexedDB(
  pageName: string,
  preferences: PagePreferences
): void {
  try {
    writePreference(pageName, preferences);
  } catch (error) {
    console.error(`Error saving preferences for ${pageName}:`, error);
  }
}

/**
 * Update specific preference keys without affecting others.
 * Reads current value, merges updates, writes back — same semantics as
 * updatePagePreferencesInCookie.
 * @param pageName Name of the page
 * @param preferenceUpdates Key-value pairs to update
 */
export function updatePagePreferencesInIndexedDB(
  pageName: string,
  preferenceUpdates: Partial<PagePreferences>
): void {
  try {
    const currentPreferences = getPagePreferencesFromIndexedDB(pageName) || {};
    const updatedPreferences = { ...currentPreferences, ...preferenceUpdates };
    setPagePreferencesInIndexedDB(pageName, updatedPreferences);
  } catch (error) {
    console.error(`Error updating preferences for ${pageName}:`, error);
  }
}

// ============================================================================
// Private storage layer
// ============================================================================

/**
 * Sync write to the in-memory Map, then fire-and-forget async IDB persist.
 *
 * WRITE STRATEGY (Fire-and-Forget):
 * The Map is updated first so the page never blocks on IndexedDB — the UI
 * stays responsive. The async write via indexedDB.ts setItem() persists
 * the "pref:<pageName>" row in the background.
 *
 * RELIABILITY:
 * setItem() resolves silently on failure (same policy as all user_data
 * writes), so this can never throw or produce an unhandled rejection. Even
 * if the write fails, the preference survives in memory for the session.
 *
 * DATA FORMAT:
 * The preferences OBJECT is stored directly (not a JSON string) — IndexedDB
 * structured-clone supports objects, which keeps the stored value readable
 * in DevTools and avoids double-encoding. The in-memory cache keeps the
 * stringified form so reads always return fresh copies.
 */
function writePreference(key: string, value: PagePreferences): void {
  prefCache.set(key, JSON.stringify(value));

  if (typeof window !== "undefined" && typeof indexedDB !== "undefined") {
    // Fire-and-forget: setItem resolves even on failure (fail-silently),
    // but .catch is kept as a safety net against future changes.
    setItem(`${PREF_KEY_PREFIX}${key}`, value).catch((error) => {
      console.warn(`[prefStore] Failed to persist "${key}" to IDB:`, error);
    });
  }
}
